import { NotebookPen } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@buildpy/ui/sheet";
import { Textarea } from "@buildpy/ui/textarea";
import { addProjectLog } from "@/api/actions/project-log";
import Field from "@/components/field";
import { isCustomerVisibleLog } from "@buildpy/core/modules/project/customer-visible";
import { MAX_LOG_BODY } from "@buildpy/core/modules/project/schema";
import { logKindsFor } from "@buildpy/core/modules/project/transitions";
import { MAX_LOG_WEATHER, PROJECT_LOG_KIND_LABEL, type ProjectLogKind, type ProjectStatus } from "@buildpy/core/modules/project/types";
import { browserDraftStore, clearLogDraft, logDraftKey, readLogDraft, writeLogDraft } from "@/modules/project/ui/log-draft";
import { useRecordAction } from "@/modules/project/ui/use-record-action";
import { photoViewKeys } from "@/modules/project/ui/use-photo-upload";
import { dropDetachedViews } from "@/lib/query";

// 서버 검사(DB char_length)와 같은 코드포인트 기준 — 카운터와 자르기가 한 함수를 쓴다.
const charsOf = (value: string): string[] => Array.from(value);
const clampBody = (value: string): string => {
  const chars = charsOf(value);
  return chars.length > MAX_LOG_BODY ? chars.slice(0, MAX_LOG_BODY).join("") : value;
};
export const clampWeather = (value: string): string => charsOf(value).slice(0, MAX_LOG_WEATHER).join("");

interface Props {
  projectId: string;
  logDate: string;
  projectStatus: ProjectStatus;
  // 그 현장 공정 — 고르면 일지에 실적(공정·투입 인원)이 쌓인다(#383 · 집계 없음).
  phases: { id: string; name: string }[];
  defaultPhaseId: string | null;
  // 시트를 여는 버튼 — 없으면 「일지 쓰기」 큰 버튼.
  trigger?: ReactNode;
  // 「붙일 사진」 줄 — 그날 올린 사진 요약(예: 「오늘 찍은 8장」).
  photoNote?: string | null;
}

// 일지 공정 — 시트에서 안 골랐으면(undefined) 지금 기본값을 따른다(촬영 화면 태그가 바뀌면 같이 바뀐다) · 「공정 없음」("")은 그대로.
export const logPhaseOf = (pick: string | undefined, defaultPhaseId: string | null): string => pick ?? defaultPhaseId ?? "";

// 투입 인원 칸은 숫자 두 자리까지만 — 서버 검사(1~99)가 마지막 판정.
const crewDigits = (value: string): string => value.replace(/\D/g, "").slice(0, 2);

// 임시 저장본은 빈 칸만 채운다 — 이미 적은 투입 인원·날씨를 덮지 않는다.
export const fillBlank = (current: string, fromDraft: string): string => (current.trim() === "" ? fromDraft : current);

// 현장일지 쓰기 — 아래에서 올라오는 시트. 종류 칩(유지보수면 무상 A/S 까지) + 본문 한 칸 · 임시 저장은 이 브라우저에만.
const LogComposeSheet = ({ projectId, logDate, projectStatus, phases, defaultPhaseId, trigger, photoNote }: Props) => {
  const { pending, run } = useRecordAction();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ProjectLogKind>("NOTE");
  const [body, setBody] = useState("");
  const [phasePick, setPhasePick] = useState<string | undefined>(undefined);
  const phaseId = logPhaseOf(phasePick, defaultPhaseId);
  const [crew, setCrew] = useState("");
  const [weather, setWeather] = useState("");
  const [saved, setSaved] = useState(false);
  const kinds = logKindsFor(projectStatus);
  const phaseOptions = [{ value: "", label: "공정 없음" }, ...phases.map((p) => ({ value: p.id, label: p.name }))];
  const draftKey = logDraftKey(projectId, logDate);
  const visible = isCustomerVisibleLog(kind);
  const kindLabelId = useId();

  // 열 때 칸이 비어 있으면 임시 저장본을 되살린다 — 고를 수 없는 종류·공정은 기본값으로.
  const changeOpen = (next: boolean) => {
    if (next && body === "") {
      const draft = readLogDraft(browserDraftStore(), draftKey);
      if (draft) {
        setKind(kinds.includes(draft.kind) ? draft.kind : "NOTE");
        setBody(clampBody(draft.body));
        setPhasePick(phases.some((p) => p.id === draft.phaseId) ? draft.phaseId : undefined);
        const draftCrew = crewDigits(draft.crew);
        const draftWeather = clampWeather(draft.weather);
        const nextCrew = fillBlank(crew, draftCrew);
        const nextWeather = fillBlank(weather, draftWeather);
        setCrew(nextCrew);
        setWeather(nextWeather);
        setSaved(nextCrew === draftCrew && nextWeather === draftWeather);
      }
    }
    setOpen(next);
  };

  // 칸을 고치면 「저장했어요」 를 다시 「임시 저장」 으로 돌린다.
  const edited = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setSaved(false);
  };

  const saveDraft = () => {
    if (writeLogDraft(browserDraftStore(), draftKey, { kind, body, phaseId, crew, weather })) setSaved(true);
    else toast.error("이 브라우저에는 임시 저장할 수 없습니다");
  };

  const submit = () =>
    void run(
      "현장일지를 저장하지 못했습니다",
      () =>
        addProjectLog(projectId, {
          log_date: logDate,
          kind,
          body,
          project_phase_id: phaseId === "" ? null : phaseId,
          crew_count: crew === "" ? null : Number(crew),
          weather: weather.trim() === "" ? null : weather.trim(),
        }),
      () => {
        toast.success("현장일지를 남겼습니다");
        dropDetachedViews(photoViewKeys(projectId));
        clearLogDraft(browserDraftStore(), draftKey);
        setBody("");
        setKind("NOTE");
        setPhasePick(undefined);
        setCrew("");
        setWeather("");
        setSaved(false);
        setOpen(false);
      }
    );

  return (
    <Sheet open={open} onOpenChange={changeOpen}>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button className="w-full" data-testid="today-log-button" size="field" variant="outline">
            <NotebookPen />
            일지 쓰기
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        isClose
        className="mx-auto flex max-h-[90dvh] max-w-xl flex-col gap-y-3 overflow-y-auto p-4"
        side="bottom">
        <SheetHeader className="flex-col items-start gap-y-1 p-0 pr-8">
          <SheetTitle>현장일지 · {logDate.slice(5)}</SheetTitle>
          <SheetDescription className="sr-only">종류를 고르고 오늘 있었던 일을 적습니다</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-y-1.5">
          <span className="text-xs text-muted-foreground" id={kindLabelId}>
            어떤 기록인가요
          </span>
          <div aria-labelledby={kindLabelId} className="flex flex-wrap gap-1.5" role="radiogroup">
            {kinds.map((k) => (
              <Button
                key={k}
                aria-checked={kind === k}
                role="radio"
                size="chip"
                type="button"
                variant="chip"
                onClick={() => edited(setKind)(k)}>
                {PROJECT_LOG_KIND_LABEL[k]}
              </Button>
            ))}
          </div>
        </div>

        <Field label="오늘 한 일">
          <Textarea
            aria-label="일지 내용"
            className="min-h-32 text-base"
            placeholder="예) 욕실 벽 타일 절반 붙였습니다. 내일 바닥 들어갑니다."
            value={body}
            onChange={(e) => edited(setBody)(clampBody(e.target.value))}
          />
        </Field>
        <div className="flex gap-x-2">
          <Field className="min-w-0 flex-1" label="오늘 공정">
            <SimpleSelect aria-label="공정" options={phaseOptions} value={phaseId} onValueChange={edited<string>(setPhasePick)} />
          </Field>
          <Field className="w-24" label="투입 인원">
            <Input
              aria-label="투입 인원"
              className="text-right tabular-nums"
              inputMode="numeric"
              name="log-crew-count"
              placeholder="명"
              value={crew}
              onChange={(e) => edited(setCrew)(crewDigits(e.target.value))}
            />
          </Field>
        </div>
        <Field label="날씨">
          <Input
            aria-label="날씨"
            data-testid="log-weather"
            name="log-weather"
            placeholder="예) 맑음, 오후 비"
            value={weather}
            onChange={(e) => edited(setWeather)(clampWeather(e.target.value))}
          />
        </Field>
        {photoNote && (
          <p className="flex justify-between gap-x-2 text-sm" data-testid="log-photo-note">
            <span className="text-muted-foreground">붙일 사진</span>
            <span className="min-w-0 truncate">{photoNote}</span>
          </p>
        )}
        <p className="text-sm text-muted-foreground" data-testid="log-visibility">
          {visible ? "보내면 고객에게 「시공일지」 로 알림이 가요" : "우리 회사 사람만 봐요"}
        </p>
        {/* 마지막 줄 아래로 안전 영역(홈 표시줄)만큼 더 띄운다. */}
        <div className="flex items-center justify-between gap-x-2 pb-safe">
          <span className="text-xs tabular-nums text-muted-foreground">
            {charsOf(body).length}/{MAX_LOG_BODY}
          </span>
          <div className="flex items-center gap-x-2">
            <Button
              data-testid="log-draft-save"
              disabled={saved || body.trim() === ""}
              type="button"
              variant="outline"
              onClick={saveDraft}>
              {saved ? "저장했어요" : "임시 저장"}
            </Button>
            <Button disabled={pending || body.trim() === ""} onClick={submit}>
              {visible ? "고객에게 보내기" : "저장하기"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LogComposeSheet;
